import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
} from "react-bootstrap";
import { FaSave, FaArrowLeft, FaCalendarAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import TestService from "../../../services/testService";
import CourseService from "../../../services/courseService";
import BatchService from "../../../services/batchService";

const EditTest = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category_id: "",
    total_questions: "",
    duration_minutes: "",
    passing_percentage: "",
    course_id: "",
    batch_id: "",
    status: "draft",
    start_date: "",
    end_date: "",
    is_scheduled: false,
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load test details
      const testResponse = await TestService.getTest(id);
      const testData = testResponse.data;

      // Load categories
      const categoriesResponse = await TestService.getCategories();
      const categoriesData = categoriesResponse.data || [];

      // Load courses
      const coursesResponse = await CourseService.getAllCourses();
      const coursesData = coursesResponse.data || [];

      // Load batches
      const batchesResponse = await BatchService.getAllBatches();
      const batchesData = batchesResponse.data || [];

      setCategories(categoriesData);
      setCourses(coursesData);
      setBatches(batchesData);

      // Format dates for datetime-local input
      let start_date = "";
      let end_date = "";
      let is_scheduled = false;

      if (testData.start_date) {
        const d = new Date(testData.start_date);
        if (!isNaN(d.getTime())) {
          start_date = d.toISOString().slice(0, 16);
          is_scheduled = true;
        }
      }
      if (testData.end_date) {
        const d = new Date(testData.end_date);
        if (!isNaN(d.getTime())) {
          end_date = d.toISOString().slice(0, 16);
        }
      }

      setFormData({
        title: testData.title || "",
        description: testData.description || "",
        category_id: testData.category_id || "",
        total_questions: testData.total_questions || "",
        duration_minutes: testData.duration_minutes || "",
        passing_percentage: testData.passing_percentage || "",
        course_id: testData.course_id || "",
        batch_id: testData.batch_id || "",
        status: testData.status || "draft",
        start_date: start_date,
        end_date: end_date,
        is_scheduled: is_scheduled,
      });
    } catch (error) {
      console.error("Failed to load test:", error);
      toast.error("Failed to load test details");
      navigate("/admin/tests");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleScheduleToggle = (e) => {
    const isChecked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      is_scheduled: isChecked,
      // Clear dates if unchecked
      start_date: isChecked ? prev.start_date : "",
      end_date: isChecked ? prev.end_date : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate total questions
    const selectedCategory = categories.find(
      (c) => c.id == formData.category_id,
    );
    if (
      selectedCategory &&
      selectedCategory.question_count < formData.total_questions
    ) {
      toast.error(
        `Category has only ${selectedCategory.question_count} questions. Please select ${formData.total_questions} or fewer questions.`,
      );
      return;
    }

    // Validate scheduled dates
    if (formData.is_scheduled) {
      if (!formData.start_date) {
        toast.error("Start date is required for scheduled test");
        return;
      }
      if (!formData.end_date) {
        toast.error("End date is required for scheduled test");
        return;
      }
      if (new Date(formData.start_date) >= new Date(formData.end_date)) {
        toast.error("End date must be after start date");
        return;
      }
    }

    try {
      setSaving(true);

      // Prepare data - convert empty strings to null for course_id and batch_id
      const submitData = {
        title: formData.title,
        description: formData.description,
        category_id: parseInt(formData.category_id),
        total_questions: parseInt(formData.total_questions),
        duration_minutes: parseInt(formData.duration_minutes),
        passing_percentage: parseFloat(formData.passing_percentage),
        course_id: formData.course_id || null,
        batch_id: formData.batch_id || null,
        status: formData.status,
        start_date: formData.is_scheduled ? formData.start_date : null,
        end_date: formData.is_scheduled ? formData.end_date : null,
      };

      console.log("Submitting data:", submitData);

      await TestService.updateTest(id, submitData);
      toast.success("Test updated successfully");
      navigate("/admin/tests");
    } catch (error) {
      console.error("Failed to update test:", error);
      toast.error(error.response?.data?.message || "Failed to update test");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading test details...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <Container className="py-4">
        <Button
          variant="link"
          className="text-decoration-none mb-3"
          onClick={() => navigate("/admin/tests")}
        >
          <FaArrowLeft className="me-2" /> Back to Tests
        </Button>

        <Card className="shadow-sm">
          <Card.Header className="bg-white">
            <h4 className="mb-0">Edit Test</h4>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Test Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="closed">Closed</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Question Category *</Form.Label>
                    <Form.Select
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name} ({cat.question_count || 0} questions)
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Total Questions *</Form.Label>
                    <Form.Control
                      type="number"
                      name="total_questions"
                      value={formData.total_questions}
                      onChange={handleChange}
                      min="1"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Duration (minutes) *</Form.Label>
                    <Form.Control
                      type="number"
                      name="duration_minutes"
                      value={formData.duration_minutes}
                      onChange={handleChange}
                      min="1"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Passing Percentage *</Form.Label>
                <Form.Control
                  type="number"
                  name="passing_percentage"
                  value={formData.passing_percentage}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  required
                />
              </Form.Group>

              {/* Scheduled Test Section */}
              <Form.Group className="mb-3">
                <Form.Label>
                  <FaCalendarAlt className="me-1 text-primary" />
                  Schedule Test (Optional)
                </Form.Label>
                <Form.Check
                  type="checkbox"
                  label="Schedule this test (set start and end time)"
                  checked={formData.is_scheduled}
                  onChange={handleScheduleToggle}
                />
              </Form.Group>

              {formData.is_scheduled && (
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Start Date & Time *</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleChange}
                        required={formData.is_scheduled}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>End Date & Time *</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleChange}
                        required={formData.is_scheduled}
                      />
                      <Form.Text className="text-muted">
                        Test will not be accessible after this time
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              <Alert variant="info">
                <small>
                  Note: The category must have at least{" "}
                  <strong>{formData.total_questions}</strong> questions available.
                  {formData.is_scheduled && (
                    <>
                      <br />
                      <strong>⏰ Scheduled:</strong> This test will be available from{" "}
                      {formData.start_date
                        ? new Date(formData.start_date).toLocaleString()
                        : "Not set"}{" "}
                      to{" "}
                      {formData.end_date
                        ? new Date(formData.end_date).toLocaleString()
                        : "Not set"}
                    </>
                  )}
                </small>
              </Alert>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Assign to Course (Optional)</Form.Label>
                    <Form.Select
                      name="course_id"
                      value={formData.course_id}
                      onChange={handleChange}
                    >
                      <option value="">No Course Assignment</option>
                      {courses.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.title}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Assign to Batch (Optional)</Form.Label>
                    <Form.Select
                      name="batch_id"
                      value={formData.batch_id}
                      onChange={handleChange}
                    >
                      <option value="">No Batch Assignment</option>
                      {batches.map((batch) => (
                        <option key={batch.id} value={batch.id}>
                          {batch.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex gap-2 mt-4">
                <Button variant="primary" type="submit" disabled={saving}>
                  <FaSave className="me-2" />
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => navigate("/admin/tests")}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default EditTest;